import * as XLSX from 'xlsx';

export interface ParsedIdsResult {
  /** 파싱된 유효 정산아이템 id (중복 제거됨) */
  ids: number[];
  /** 파싱 과정에서 무시된 행의 정보. UI에 경고로 표시 */
  invalidRows: Array<{ row: number; value: unknown; reason: string }>;
  /** 전체 스캔한 데이터 행 수(헤더 제외) */
  totalRows: number;
}

/**
 * 정산아이템 id 리스트 엑셀 파일을 파싱한다.
 *
 * 가이드 문서의 2차 작업(엑셀 `정산아이템_리스트.xlsx` 기준) 패턴에 대응.
 * 첫 시트의 첫 컬럼에서 숫자 id를 추출한다.
 * - 헤더가 'id' / 'ID' / '정산 id' 등 비-숫자면 자동 스킵
 * - 빈 셀 / 비-숫자 값은 invalidRows에 기록하고 스킵 (에러 대신 경고)
 * - 중복 id는 한 번만 반환
 *
 * 지원 확장자: .xlsx .xls .csv (xlsx 라이브러리 기본 지원)
 *
 * @throws 파일 자체가 읽을 수 없는 경우만 에러. 개별 셀 문제는 invalidRows로 보고.
 */
export async function parseSettlementIdExcel(
  file: File,
): Promise<ParsedIdsResult> {
  const buffer = await readFileAsArrayBuffer(file);
  const workbook = XLSX.read(buffer, { type: 'array' });

  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error('엑셀 파일에 시트가 없습니다.');
  }
  const sheet = workbook.Sheets[firstSheetName];

  // 첫 컬럼만 A열로 추출. header:1 = 1차원 배열 모드.
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: null,
    blankrows: false,
  });

  if (rows.length === 0) {
    return { ids: [], invalidRows: [], totalRows: 0 };
  }

  // 첫 행 첫 셀이 숫자면 헤더가 없다고 판단, 아니면 건너뜀.
  const firstCell = rows[0][0];
  const hasHeader = !isNumericLike(firstCell);
  const dataRows = hasHeader ? rows.slice(1) : rows;

  const seen = new Set<number>();
  const ids: number[] = [];
  const invalidRows: ParsedIdsResult['invalidRows'] = [];

  dataRows.forEach((row, idx) => {
    // 실제 엑셀의 행 번호(1-based). 헤더 있으면 idx=0 → 행 2.
    const excelRow = idx + 1 + (hasHeader ? 1 : 0);
    const value = row?.[0];

    if (value === null || value === undefined || value === '') {
      // 완전 빈 행은 조용히 스킵
      return;
    }

    if (!isNumericLike(value)) {
      invalidRows.push({
        row: excelRow,
        value,
        reason: '숫자가 아닙니다',
      });
      return;
    }

    const settlementItemId = Number(value);
    if (!Number.isInteger(settlementItemId) || settlementItemId <= 0) {
      invalidRows.push({
        row: excelRow,
        value,
        reason: '양의 정수가 아닙니다',
      });
      return;
    }

    if (seen.has(settlementItemId)) {
      // 중복은 조용히 스킵 (정상 케이스)
      return;
    }
    seen.add(settlementItemId);
    ids.push(settlementItemId);
  });

  return {
    ids,
    invalidRows,
    totalRows: dataRows.length,
  };
}

/**
 * File을 ArrayBuffer로 읽는다. 모던 브라우저의 `file.arrayBuffer()`는 간결하지만
 * jsdom(jest 환경)에선 구현이 빠져있는 경우가 있어 FileReader로 감싼다.
 */
function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error('ArrayBuffer 로 읽히지 않았습니다.'));
      }
    };
    reader.onerror = (): void =>
      reject(reader.error ?? new Error('파일 읽기 실패'));
    reader.readAsArrayBuffer(file);
  });
}

const NUMERIC_PATTERN = /^-?\d+(\.\d+)?$/;

/** 숫자 또는 숫자 문자열("123", "123 ")인지 검사. */
const isNumericLike = (v: unknown): boolean => {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v !== 'string') return false;
  return NUMERIC_PATTERN.test(v.trim());
};
